import { datadogRum } from '@datadog/browser-rum-slim';
import { DatadogAdsEvents } from '@eventbrite/ads';
import { CoreApplicationContext } from '@eventbrite/context-gen';
import { AlertChunky } from '@eventbrite/eds-iconography';
import { Layout } from '@eventbrite/eds-layout';
import { TYPE_NEUTRAL } from '@eventbrite/eds-notification';
import {
    withMainControls,
    WithMainControlsProps,
    withOverlayControls,
} from '@eventbrite/eds-structure';
import { getLocationSuggestions } from '@eventbrite/location-autocomplete';
import { addCustomRUMEvent } from '@eventbrite/real-user-monitoring';
import { $FixMe } from '@eventbrite/ts-utils';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { StickyContainer } from 'react-sticky';
import { useMount } from 'react-use';
import { interleaveTwoArrays } from '../../../../../../ads/src/tracking/interleaveTwoArrays';
import { SeasonalRibbon } from '../../../../components/SeasonalRibbon';
import {
    COLLECTION_TYPE,
    ENTITY_TYPE_MAP,
    EVENT_CARD_TYPE,
    LOCATION_DIALOG_CONTENT,
} from '../../../../constants';
import { NEIGHBOURHOOD } from '../../../../constants/neighbourhoodMapping';
import { trackCityBrowsePageView } from '../../../../redux/actions/analytics';
import {
    getUserCurrentLocation as getUserCurrentLocationAction,
    locationChange as locationChangeAction,
    searchLocationSuggestions as searchLocationSuggestionsAction,
} from '../../../../redux/actions/location';
import {
    BrandState,
    Bucket,
    BucketsContent,
    ConnectedProps,
    DiscoverState,
} from '../../../../types/index';
import { CityBrowseBanner } from '../../components/CityBrowseBanner';
import { getCityBrowseAdSlots } from '../../constants/adSlotsConfig';
import { getLocalizedBrandOverrides, getMaskState } from '../../utils';
import { BreadCrumbContainer } from '../BreadCrumbContainer';
import { CITY_BROWSE_PLACEMENT_NAME } from '../BrowseBucket/tracking';
import { MainContent, MainContentDataLayer } from '../MainContent';
import { NeighbourhoodContainer } from '../NeighbourhoodContainer';

const DEFAULT_BUCKET_SIZE = 8;

const filterCollectionsFromBuckets = (buckets: Bucket[]) => {
    if (buckets !== undefined) {
        return buckets.filter((bucket) => bucket.type !== COLLECTION_TYPE);
    }
    return [];
};

const updateBuckets = (buckets: Bucket[]) => {
    return buckets.reduce((acc: BucketsContent, bucket) => {
        const contentKey = ENTITY_TYPE_MAP[bucket.type];
        const maybeEvents = bucket[contentKey];
        const promotedEvents = bucket.promoted_events || [];
        const adSlots = getCityBrowseAdSlots(bucket.key);

        if (
            bucket.type !== EVENT_CARD_TYPE ||
            isEmpty(promotedEvents) ||
            !maybeEvents ||
            adSlots.length === 0
        ) {
            acc.push(bucket);
            return acc;
        }

        // Use interleaveTwoArrays to insert promoted events at configured positions
        // adSlots is expected to be an array of positions, which are 1-indexed
        const interleavedEvents = interleaveTwoArrays(
            maybeEvents,
            promotedEvents,
            adSlots,
        );

        // Clear the original array and push the interleaved events
        // cannot do something like this bucket[contentKey] = interleavedEvents;
        // because this will cause a reference update and a rerender
        maybeEvents.length = 0;
        maybeEvents.push(...interleavedEvents);

        // Apply bucket size limit, idealy bucket size would be provided
        if (maybeEvents.length > DEFAULT_BUCKET_SIZE) {
            maybeEvents.splice(DEFAULT_BUCKET_SIZE);
        }

        acc.push(bucket);

        return acc;
    }, []);
};

interface OwnProps {
    history: any;
    /*
     * Array of location suggestions for consumption by
     * the location switcher
     */
    env: CoreApplicationContext['env'];
    locationSuggestions?: {
        suggestions?: $FixMe;
        hasSuggestions?: boolean;
    };
    showOverlay?: Function;
    buckets: BucketsContent;
    moreBuckets: BucketsContent;
    excludedBuckets: BucketsContent;
    cityPlaceId?: string;
    location_slug?: string;
    current_page: string;
}

export const CityBrowseBaseScreen = (props: CityBrowseBaseScreenProps) => {
    const {
        env,
        brand = {} as BrandState,
        cityBlurb,
        country,
        currentPlace,
        featureFlags,
        h1Prefix,
        history,
        handleLocationSelect,
        isOnline,
        onTrackPageViewAction,
        locationDenied,
        handleUseCurrentLocation,
        isBotRequest,
        showOverlay,
        buckets,
        moreBuckets,
        excludedBuckets,
        placeId,
        cityPlaceId,
        slug,
        addMainNotification,
        hideMainNotification,
        showErrorNotification,
    } = props;
    const {
        decorationBottomLeftUrl,
        decorationBottomRightUrl,
        decorationTopLeftUrl,
        decorationTopRightUrl,
        headerImageUrl,
        headerSmallImageUrl,
        middleImageUrl,
        middleSmallImageUrl,
    } = brand;
    useMount(() => {
        onTrackPageViewAction();
    });
    const isMobile = !!env?.isMobile;
    useEffect(() => {
        if (locationDenied) {
            showOverlay?.('dialog', LOCATION_DIALOG_CONTENT);
        }
    }, [locationDenied, showOverlay]);

    useEffect(() => {
        if (showErrorNotification) {
            addMainNotification({
                type: TYPE_NEUTRAL,
                children: showErrorNotification,
                iconType: <AlertChunky />,
                hasCloseButton: true,
            });
        } else {
            hideMainNotification();
        }
    }, [showErrorNotification, addMainNotification, hideMainNotification]);

    const locationData = {
        slug,
        country,
        currentPlace,
        isOnline,
        placeId,
        cityPlaceId,
    };
    const bucketWithoutCollections = filterCollectionsFromBuckets(buckets);
    const bucketsUpdatedWithPromotedEvents = updateBuckets(
        bucketWithoutCollections,
    );

    useDataDogAdTracking(buckets, isMobile);

    return (
        <Layout>
            <StickyContainer style={{ overflowY: 'auto' }}>
                <SeasonalRibbon
                    season="holidays"
                    locationSlug={props.location_slug || 'online'}
                    country={props.country}
                    browseSurface={'city-browse'}
                    isMobile={env?.isMobile}
                />
                <BreadCrumbContainer />
                <CityBrowseBanner
                    currentPlace={currentPlace}
                    cityBlurb={cityBlurb}
                    h1Prefix={h1Prefix}
                    cityImage={headerImageUrl}
                    citySmallImage={headerSmallImageUrl}
                    history={props.history}
                    isBotRequest={isBotRequest}
                    handleUseCurrentLocation={() =>
                        handleUseCurrentLocation?.(history, true)
                    }
                    handleLocationSelect={handleLocationSelect}
                />
                <MainContentDataLayer
                    placeId={placeId}
                    cityPlaceId={cityPlaceId}
                    isOnline={isOnline}
                    buckets={bucketsUpdatedWithPromotedEvents}
                    excludedBuckets={excludedBuckets}
                    moreBuckets={moreBuckets}
                >
                    {featureFlags.enableNeighbourhoodCityBrowse &&
                        NEIGHBOURHOOD[locationData.placeId || ''] && (
                            <NeighbourhoodContainer
                                locationData={locationData}
                            />
                        )}

                    <MainContent
                        middleImage={middleImageUrl || ''}
                        middleSmallImage={middleSmallImageUrl || ''}
                        decorationTopLeftUrl={decorationTopLeftUrl || ''}
                        decorationTopRightUrl={decorationTopRightUrl || ''}
                        decorationBottomLeftUrl={decorationBottomLeftUrl || ''}
                        decorationBottomRightUrl={
                            decorationBottomRightUrl || ''
                        }
                        featureFlags={featureFlags}
                        locationData={locationData}
                        locale={env?.localeInfo?.locale}
                        usesLanguageSubdirectory={
                            env?.localeInfo?.uses_language_subdirectory
                        }
                    />
                </MainContentDataLayer>
            </StickyContainer>
        </Layout>
    );
};

function useDataDogAdTracking(
    cityBrowseSSRBuckets: BucketsContent = [],
    isMobile: boolean,
) {
    useEffect(() => {
        const bucketsPromotedEventsCount = cityBrowseSSRBuckets
            .filter((b) => b.type === 'event')
            .reduce((acc, b) => acc + (b.promoted_events?.length || 0), 0);

        if (bucketsPromotedEventsCount === 0) {
            return;
        }

        datadogRum.onReady(() => {
            addCustomRUMEvent(DatadogAdsEvents.SSRObtained, {
                value: bucketsPromotedEventsCount,
                app: 'web',
                is_mobile: isMobile,
                interface: CITY_BROWSE_PLACEMENT_NAME,
            });
        });
    }, [cityBrowseSSRBuckets, isMobile]);
}

export const _mapStateToProps = (
    state: DiscoverState,
    _ownProps?: OwnProps,
) => {
    const brand = getLocalizedBrandOverrides(
        state.brand,
        state.location,
        state.app.tld,
    );
    return {
        country: state.location.country,
        currentPlace: state.location.currentPlace,
        h1Prefix: state.location.h1Prefix,
        cityBlurb: state.location.cityBlurb,
        slug: state.location.slug,
        placeId: state.location.placeId,
        cityPlaceId: _ownProps?.cityPlaceId,
        locationSuggestions: getLocationSuggestions(
            state.location,
            state.app.featureFlags?.useCurrentLocation,
        ),
        shouldHideMask: getMaskState(brand, state.location.isOnline),
        domain: state.app.domain,
        featureFlags: state.app.featureFlags,
        experiments: state.app.experiments,
        locationDenied: state.location.locationDenied,
        isWaitingForLocation: state.location.isWaitingForLocation,
        isOnline: state.location.isOnline,
        isBotRequest: state.app.isBotRequest,
        brand,
        showErrorNotification: state.ui.showErrorNotification,
    };
};

const _mapDispatchToProps = (dispatch: Function, ownProps: OwnProps) => ({
    /*
     * Callback to display updated autocomplete suggestions
     */
    handleLocationSuggestionChange: (...args: [$FixMe]) =>
        dispatch(searchLocationSuggestionsAction(...args)),
    /*
     * Callback for handling changing of the selected metro
     */
    handleLocationSelect: (locationChangeObj = {}) =>
        dispatch(
            locationChangeAction({
                ...locationChangeObj,
                history: ownProps.history,
            }),
        ),
    onTrackPageViewAction: () => dispatch(trackCityBrowsePageView()),
    handleUseCurrentLocation: (...args: any) =>
        dispatch(getUserCurrentLocationAction(...args)),
});
type ReduxProps = ConnectedProps<
    typeof _mapStateToProps,
    typeof _mapDispatchToProps
>;
type CityBrowseBaseScreenProps = ReduxProps & OwnProps & WithMainControlsProps;
const ConnectedComponent = connect(
    _mapStateToProps,
    _mapDispatchToProps,
)(CityBrowseBaseScreen);

export const CityBrowseBaseScreenContainer = withOverlayControls(
    withMainControls(
        withRouter(ConnectedComponent as $FixMe) as $FixMe,
    ) as $FixMe,
);
